import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ManualEntry, Product, Quarry } from '../types/catalog';
import { quarries } from '../data/quarries';
import { destinations } from '../data/destinations';
interface ManualPriceFormProps {
  product: Product;
  onAdd: (entry: ManualEntry) => void;
  onCancel: () => void;
}
const OTHER = 'other';
const fieldClass = 'h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm text-ink outline-none transition-colors duration-150 focus:border-accent';
const labelClass = 'mb-1 block text-xs font-medium uppercase tracking-wide text-ink-soft';
export function ManualPriceForm({
  product,
  onAdd,
  onCancel
}: ManualPriceFormProps) {
  const [quarryId, setQuarryId] = useState(quarries[0].id);
  const [customName, setCustomName] = useState('');
  const [customAddress, setCustomAddress] = useState('');
  const [customCity, setCustomCity] = useState(destinations[0].label);
  const [price, setPrice] = useState('');
  const [minLoad, setMinLoad] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isCustom = quarryId === OTHER;
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsedPrice = Number.parseFloat(price);
    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      setError('Enter a price greater than zero.');
      return;
    }
    if (isCustom && !customName.trim()) {
      setError('Name the pit or plant this price came from.');
      return;
    }
    let quarry: Quarry;
    if (isCustom) {
      const city = destinations.find(d => d.label === customCity) ?? destinations[0];
      quarry = {
        id: `custom-${Date.now()}`,
        name: customName.trim(),
        operator: 'Entered by hand',
        siteCode: '—',
        address: customAddress.trim(),
        city: city.label.split(',')[0],
        state: 'WA',
        zip: city.zip,
        phone: '',
        hours: 'Hours unknown',
        coords: city.coords,
        surchargePerUnit: 0,
        pricingNote: 'Entered by hand'
      };
    } else {
      quarry = quarries.find(q => q.id === quarryId)!;
    }
    onAdd({
      id: `entry-${Date.now()}`,
      productId: product.id,
      quarry,
      price: parsedPrice,
      minLoadCharge: Math.max(0, Number.parseFloat(minLoad) || 0),
      note: note.trim()
    });
  }
  return <motion.form initial={{
    opacity: 0,
    y: -4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} onSubmit={handleSubmit} className="mt-4 rounded-xl border border-line bg-surface p-5" aria-label={`Add a price for ${product.name}`} data-id="element-60" data-mp="37s41rne550sd">
      <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-61" data-mp="32ssj28u7rl85">
        <h3 className="text-base font-semibold text-ink" data-id="element-62" data-mp="x0vhsifhydr0">Add a phoned-in price</h3>
        <p className="text-sm text-ink-muted" data-id="element-63" data-mp="wyrbjtu4l2nk">
          {product.name} · per {product.unit}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4" data-id="element-64" data-mp="2ljyjjuvr7l1n">
        <div className="md:col-span-2" data-id="element-65" data-mp="2li5f8m8m5cwt">
          <label className={labelClass} htmlFor="manual-quarry" data-id="element-66" data-mp="2hj68cw3bn62q">
            Pit or plant
          </label>
          <select id="manual-quarry" value={quarryId} onChange={event => setQuarryId(event.target.value)} className={fieldClass} data-id="element-67" data-mp="2vab1dfc6p043">
            {quarries.map(quarry => <option key={quarry.id} value={quarry.id} data-id="element-68" data-mp="356e4ooirv4sm">
                {quarry.name} — {quarry.city}
              </option>)}
            <option value={OTHER} data-id="element-69" data-mp="3e8netda4ow5h">Other pit or plant…</option>
          </select>
        </div>

        {isCustom && <>
            <div data-id="element-70" data-mp="39avjsae6stx2">
              <label className={labelClass} htmlFor="manual-name" data-id="element-71" data-mp="39ac6x83e8vpw">
                Name
              </label>
              <input id="manual-name" value={customName} onChange={event => setCustomName(event.target.value)} placeholder="e.g. Cadman Monroe" className={fieldClass} data-id="element-72" data-mp="1jv4ig6ru3jlr" />
            </div>
            <div data-id="element-73" data-mp="1lagpa21bunub">
              <label className={labelClass} htmlFor="manual-address" data-id="element-74" data-mp="1le4lxa22jqd9">
                Street address (optional)
              </label>
              <input id="manual-address" value={customAddress} onChange={event => setCustomAddress(event.target.value)} placeholder="e.g. 14200 SE 288th St" className={fieldClass} data-id="element-75" data-mp="1v9z7oe1la1k9" />
            </div>
            <div data-id="element-76" data-mp="17kwtjg65yqui">
              <label className={labelClass} htmlFor="manual-city" data-id="element-77" data-mp="17kd8vrp4pocs">
                City
              </label>
              <select id="manual-city" value={customCity} onChange={event => setCustomCity(event.target.value)} className={fieldClass} data-id="element-78" data-mp="1ccesxzsjgjdd">
                {destinations.map(destination => <option key={destination.zip} value={destination.label} data-id="element-79" data-mp="cb9rpvb5urac">
                    {destination.label}
                  </option>)}
              </select>
            </div>
          </>}

        <div data-id="element-80" data-mp="2cc3kthnczrv">
          <label className={labelClass} htmlFor="manual-price" data-id="element-81" data-mp="2c29outhe8gw">
            Price / {product.unit}
          </label>
          <input id="manual-price" inputMode="decimal" value={price} onChange={event => setPrice(event.target.value)} placeholder="0.00" className={`${fieldClass} tnum`} data-id="element-82" data-mp="3tjewbpd4ve47" />
        </div>

        <div data-id="element-83" data-mp="3ftbct65n1yig">
          <label className={labelClass} htmlFor="manual-min" data-id="element-84" data-mp="3fsrv9qjwhc4y">
            Minimum load ($)
          </label>
          <input id="manual-min" inputMode="decimal" value={minLoad} onChange={event => setMinLoad(event.target.value)} placeholder="100" className={`${fieldClass} tnum`} data-id="element-85" data-mp="3av04izuzrw2c" />
        </div>

        <div className="md:col-span-4" data-id="element-86" data-mp="2r6c21p8wwnzj">
          <label className={labelClass} htmlFor="manual-note" data-id="element-87" data-mp="2r9hu9dcu85zh">
            Note (optional)
          </label>
          <input id="manual-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Who quoted it, quote date, availability…" className={fieldClass} data-id="element-88" data-mp="2w43v4mlamg5b" />
        </div>
      </div>

      {error && <p role="alert" className="mt-3 text-sm text-accent" data-id="element-89" data-mp="3axh14d4a6szm">
          {error}
        </p>}

      <div className="mt-5 flex items-center gap-2" data-id="element-90" data-mp="3ez2f61sjxdbe">
        <button type="submit" className="rounded-lg bg-ink px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-accent" data-id="element-91" data-mp="314i4q92jly5g">
          Save price
        </button>
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-medium text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-92" data-mp="3tsnp2upopl4r">
          Cancel
        </button>
      </div>
    </motion.form>;
}